<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { capFirstChar, calculateTokenWidthByTitle } from '../../../../helpers/utilities';
	import {
		onSettingsResponse,
		getApplianceTypes,
		getReturnedEnergyTypes,
		castUsageStringToRelayUsageParam,
	} from '../../../../helpers/device_settings_helpers';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	//Design system components
	import { Button, Token, Text, toast } from '@Shelly/shelly-design-system';
	//Types
	import type { ApplianceType } from '../../../../helpers/device_settings_helpers';
	import type { Device } from '../../../../types/Device';
	//Svelte hooks
	import { onMount } from 'svelte';
	import { postReq } from 'src/services/http';
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;

	const types: ApplianceType[] = getApplianceTypes()
		.filter((at) => at.value !== 0)
		.map((at) => {
			at.title = capFirstChar(at.title);
			return at;
		});
	// will cap first until 'text-capitalize' class can be applied to <Select> options from outside

	const sources: ApplianceType[] = getReturnedEnergyTypes()
		.filter((at) => at.value !== 0)
		.map((at) => {
			at.title = capFirstChar(at.title);
			return at;
		});

	const save = async () => {
		if (!selected || device.relay_usage === selected.usage) {
			toast.open({
				type: 'info',
				title: $format('toast_select_different_appliance_type', {
					default: 'Please select a different appliance type to change it.',
				}),
			});
			return;
		}

		waitForResponse = true;

		device.appliance_type = selected?.value;
		device.relay_usage = castUsageStringToRelayUsageParam(selected);
		const res = await requestEditDevice(device);

		if (res.isok) {
			if (device.relay_usage !== 'light') {
				const internalRes = await postReq('interface/device/set_cloud_options', {
					id: device.id,
					set: 'exclude_forgotten_lights',
					val: true,
				});
				if (internalRes.isok) {
					device.cloud_options.exclude_forgotten_lights = true;
					devices.update(device);
				}
			}
		}

		if (!initialSave) {
			onSettingsResponse(res, device, {
				success: selected?.title
					? $format('appliance_type_scc_toast', {
							values: { value: selected?.title },
							default: `Appliance type was changed to ${selected?.title}.`,
						})
					: $format('appliance_type_disabled_scc_toast', {
							default: 'Appliance type was disabled.',
						}),
			});
		} else {
			initialSave = false;
		}
		waitForResponse = false;
	};

	let selected: ApplianceType | undefined =
			types.find((type) => type.usage === device.relay_usage) ||
			sources.find((source) => source.usage === device.relay_usage),
		waitForResponse = false,
		initialSave = false,
		selectedSource =
			sources.find((at) => at.usage == device.relay_usage) ||
			sources.find((at) => at.usage == 'other');

	const selectType = (type: ApplianceType) => {
		type.selected = !type.selected;
		selected = type;
	};

	onMount(() => {
		if (typeof selected?.usage === 'undefined') {
			selected = types.filter((at) => at.usage == 'other')[0];
			initialSave = true;
			save();
		}
		if (typeof selectedSource !== 'undefined') {
			selected = selectedSource;
		}
	});
</script>

<Text styleType="body" class="mb-m">
	{$format('title_appliance_types', { default: 'Appliance types' })}
</Text>
<div class="d-flex flex-wrap gap-s mb-m">
	{#each types as type}
		<Token
			id="token-s5y8fsj"
			size="m"
			title={type.title}
			class="text-nowrap"
			width={$showNewLayout ? undefined : calculateTokenWidthByTitle(type.title)}
			icon={`fas fa-${type.icon}`}
			selected={selected?.usage === type?.usage}
			on:click={() => {
				selectType(type);
			}}
		/>
	{/each}
</div>
<Text styleType="body" class="mb-m">
	{$format('title_consumption_source', { default: 'Source' })}
</Text>
<div class="d-flex flex-wrap gap-s mb-m">
	{#each sources as source}
		<Token
			id="token-5xixny6"
			size="m"
			title={source.title}
			class="text-nowrap"
			width={$showNewLayout ? undefined : calculateTokenWidthByTitle(source.title)}
			icon={source.icon}
			selected={selected?.usage === source?.usage}
			on:click={() => {
				selectType(source);
			}}
		/>
	{/each}
</div>
<Button size="m" width="full" loading={waitForResponse} on:click={save}
	>{$format('button_save', { default: 'save' })}</Button
>
